import { cn } from "@faire/react/tools/cn";
import { MessagingBanner } from "@faire/slate/components/banner/messaging";
import { Button } from "@faire/slate/components/button";
import type { ReactNode } from "react";

import type { CompassProduct } from "../../../contexts/CompassContext";
import { compassProducts } from "../../../view_models/compassProducts";

const CATEGORIES = [
  "Bestsellers",
  "Home decor",
  "Food & drink",
  "Beauty & wellness",
  "Women",
] as const;

const FEATURED_PRODUCTS = compassProducts.slice(0, 8);

interface RetailerHomepageProps {
  shoppingFeedback: string | null;
  onDismissFeedback: () => void;
}

export const RetailerHomepage = ({
  shoppingFeedback,
  onDismissFeedback,
}: RetailerHomepageProps): ReactNode => (
  <main className="mx-auto flex min-h-screen w-full max-w-screen-2xl flex-col gap-(--slate-spacing-section-large) px-(--slate-spacing-layout-edge-mobile) py-(--slate-spacing-section-medium) md:px-(--slate-spacing-layout-edge-tablet) lg:px-(--slate-spacing-layout-edge-desktop-default)">
    <section className="flex flex-col gap-(--slate-spacing-group-large)">
      <div className="flex flex-col gap-(--slate-spacing-element-small)">
        <p className="type-fs-paragraph text-fs-text-subdued">Good morning</p>
        <h1 className="type-fs-page-heading text-fs-text-primary">
          Welcome back, Vikas
        </h1>
      </div>
      <div className="flex flex-wrap gap-(--slate-spacing-element-small)">
        {CATEGORIES.map((category) => (
          <Button key={category} size="xSmall" variant="secondary">
            {category}
          </Button>
        ))}
      </div>
    </section>

    <section
      aria-label="Homepage highlights"
      className="grid gap-(--slate-spacing-group-large) md:grid-cols-3"
    >
      <HighlightCard
        surface="orange"
        eyebrow="Trending now"
        title="New season, fresh arrivals"
        description="Explore this week’s most-loved home and gift finds."
      />
      <HighlightCard
        surface="neutral"
        eyebrow="Faire Market"
        title="Build your summer collection"
        description="Shop standout brands selected for independent retailers."
      />
      <HighlightCard
        surface="insider"
        eyebrow="Insider"
        title="Free shipping on thousands of brands"
        description="Look for the Insider key while you shop."
      />
    </section>

    <section
      id="welcome-offer-products"
      className="scroll-mt-(--slate-spacing-section-medium)"
    >
      <div className="flex flex-col gap-(--slate-spacing-group-large)">
        <div className="flex flex-col gap-(--slate-spacing-element-small)">
          <h2 className="type-fs-section-heading text-fs-text-primary">
            Bestsellers for your first order
          </h2>
          <p className="type-fs-paragraph text-fs-text-subdued">
            Products retailers are adding to their shelves right now.
          </p>
        </div>
        {shoppingFeedback ? (
          <MessagingBanner
            variant="success"
            label="Welcome offer ready"
            description={shoppingFeedback}
            onDismiss={onDismissFeedback}
          />
        ) : null}
        <div className="grid grid-cols-2 gap-x-(--slate-spacing-group-large) gap-y-(--slate-spacing-section-medium) md:grid-cols-3 xl:grid-cols-4">
          {FEATURED_PRODUCTS.map((product) => (
            <ProductCard key={product.token ?? product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  </main>
);

const HighlightCard = ({
  surface,
  eyebrow,
  title,
  description,
}: {
  surface: "orange" | "neutral" | "insider";
  eyebrow: string;
  title: string;
  description: string;
}): ReactNode => (
  <article
    className={cn(
      "flex flex-col justify-between gap-(--slate-spacing-group-large) rounded-(--slate-radius-container-default) p-(--slate-spacing-element-xxlarge)",
      {
        "bg-fs-expressive-surface-orange": surface === "orange",
        "bg-fs-expressive-surface-neutral": surface === "neutral",
        "bg-fs-program-surface-insider-primary": surface === "insider",
      }
    )}
  >
    <div className="flex flex-col gap-(--slate-spacing-element-small)">
      <p className="type-fs-label-medium text-fs-text-primary">{eyebrow}</p>
      <h2 className="type-fs-subheading-medium text-fs-text-primary">
        {title}
      </h2>
      <p className="type-fs-paragraph text-fs-text-primary">{description}</p>
    </div>
    <span className="type-fs-label-medium text-fs-text-primary underline">
      Shop now
    </span>
  </article>
);

const ProductCard = ({ product }: { product: CompassProduct }): ReactNode => (
  <article className="flex min-w-0 flex-col gap-(--slate-spacing-element-medium)">
    <div className="bg-fs-surface-secondary border-fs-border-muted aspect-[4/3] overflow-hidden rounded-(--slate-radius-container-default) border">
      <img
        alt={product.name}
        className="h-full w-full object-cover"
        loading="lazy"
        src={product.imageUrl}
      />
    </div>
    <div className="flex flex-col gap-(--slate-spacing-element-xsmall)">
      <p className="type-fs-label-medium text-fs-text-subdued">
        {product.brandName}
      </p>
      <h3 className="type-fs-paragraph-medium text-fs-text-primary line-clamp-2">
        {product.name}
      </h3>
      <p className="type-fs-paragraph text-fs-text-primary">
        ${product.priceDollars ?? product.price} wholesale
      </p>
      <p className="type-fs-label text-fs-text-subdued">
        ★ {product.rating ?? 4.9} · ${product.brandMinimum ?? 100} minimum
      </p>
    </div>
  </article>
);
